import { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

/**
 * Reusable resizable panel wrapper.
 * Renders a draggable handle on the specified edge to resize the panel.
 *
 * Props:
 *  - edge: "right" | "left" — which side the drag handle appears on
 *  - defaultWidth: initial width in px (default 360)
 *  - minWidth: minimum width in px (default 240)
 *  - maxWidth: maximum width in px (default 600)
 *  - children: panel content
 *  - sx: additional sx on the outer container
 */
const ResizablePanel = ({
  edge = "right",
  defaultWidth = 360,
  minWidth = 240,
  maxWidth = 600,
  fillRemaining = false,
  children,
  sx: sxProp,
}) => {
  const isFixedWidth = minWidth === maxWidth;
  const [width, setWidth] = useState(defaultWidth);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [width]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging.current) return;
      const delta = edge === "right"
        ? e.clientX - startX.current
        : startX.current - e.clientX;
      const next = Math.min(maxWidth, Math.max(minWidth, startWidth.current + delta));
      setWidth(next);
    };

    const onMouseUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [edge, minWidth, maxWidth]);

  const handle = (
    <Box
      onMouseDown={onMouseDown}
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        [edge]: -4,
        width: 8,
        cursor: "col-resize",
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&::after": {
          content: '""',
          width: 3,
          height: 32,
          borderRadius: 2,
          backgroundColor: "#D1D5DB",
          transition: "background-color 0.15s ease",
        },
        "&:hover::after": { backgroundColor: "#3B82F6" },
      }}
    />
  );

  return (
    <Box
      sx={{
        position: "relative",
        ...(fillRemaining
          ? { flex: 1, minWidth: 0 }
          : { width, flexShrink: 0 }),
        height: "100%",
        ...sxProp,
      }}
    >
      {children}
      {!fillRemaining && !isFixedWidth && handle}
    </Box>
  );
};

export default ResizablePanel;
